import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ExportProcess = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const processSteps = [
    {
      id: 1,
      title: "Initial Consultation",
      subtitle: "Understanding Your Needs",
      duration: "30-60 minutes",
      description: "We begin with a comprehensive consultation to understand your specific product requirements, quality standards, budget constraints, and delivery timelines.",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop",
      icon: "MessageCircle",
      activities: [
        "Product specification review",
        "Quality requirements assessment",
        "Budget and timeline discussion",
        "Market compliance requirements",
        "Custom packaging needs"
      ],
      deliverables: [
        "Detailed requirement document",
        "Preliminary cost estimate",
        "Timeline proposal",
        "Compliance checklist"
      ],
      nextSteps: "Supplier identification and vetting process begins"
    },
    {
      id: 2,
      title: "Supplier Sourcing",
      subtitle: "Finding the Perfect Match",
      duration: "2-5 days",
      description: "Our extensive network of pre-vetted African suppliers is searched to find the perfect match for your requirements, ensuring quality and reliability.",
      image: "https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg?w=600&h=400&fit=crop",
      icon: "Search",
      activities: [
        "Supplier database search",
        "Capability assessment",
        "Quality verification",
        "Pricing negotiation",
        "Sample procurement"
      ],
      deliverables: [
        "Supplier profiles",
        "Product samples",
        "Detailed quotations",
        "Quality certificates"
      ],
      nextSteps: "Quality assurance and testing phase"
    },
    {
      id: 3,
      title: "Quality Assurance",
      subtitle: "Ensuring Excellence",
      duration: "3-7 days",
      description: "Comprehensive quality control processes including pre-shipment inspections, testing, and certification to guarantee product excellence.",
      image: "https://images.pixabay.com/photo/2016/04/14/18/29/gold-1329217_1280.jpg?w=600&h=400&fit=crop",
      icon: "Shield",
      activities: [
        "Pre-shipment inspection",
        "Quality testing protocols",
        "Compliance verification",
        "Documentation review",
        "Final approval process"
      ],
      deliverables: [
        "Quality inspection report",
        "Test certificates",
        "Compliance documentation",
        "Photo documentation"
      ],
      nextSteps: "Export documentation preparation"
    },
    {
      id: 4,
      title: "Documentation",
      subtitle: "Paperwork Perfection",
      duration: "2-4 days",
      description: "Complete preparation of all export documentation including certificates of origin, quality certificates, and customs paperwork.",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop",
      icon: "FileText",
      activities: [
        "Export license preparation",
        "Certificate of origin",
        "Quality documentation",
        "Customs declarations",
        "Insurance documentation"
      ],
      deliverables: [
        "Complete document package",
        "Digital copies",
        "Tracking numbers",
        "Insurance certificates"
      ],
      nextSteps: "Professional packaging and labeling"
    },
    {
      id: 5,
      title: "Packaging & Shipping",
      subtitle: "Safe & Secure Transit",
      duration: "1-3 days prep + 14-21 days transit",
      description: "Professional packaging designed for international shipping with proper labeling, followed by coordinated shipping through our global logistics network.",
      image: "https://images.pexels.com/photos/6069112/pexels-photo-6069112.jpeg?w=600&h=400&fit=crop",
      icon: "Package",
      activities: [
        "Export-grade packaging",
        "International labeling",
        "Freight coordination",
        "Customs clearance",
        "Real-time tracking setup"
      ],
      deliverables: [
        "Tracking information",
        "Shipping documents",
        "Insurance coverage",
        "Delivery schedule"
      ],
      nextSteps: "Delivery and post-delivery support"
    },
    {
      id: 6,
      title: "Delivery & Support",
      subtitle: "Complete Satisfaction",
      duration: "Ongoing",
      description: "Final delivery confirmation with comprehensive post-delivery support, feedback collection, and relationship management for future orders.",
      image: "https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=600&h=400&fit=crop",
      icon: "CheckCircle",
      activities: [
        "Delivery confirmation",
        "Quality feedback collection",
        "Issue resolution",
        "Relationship management",
        "Future order planning"
      ],
      deliverables: [
        "Delivery confirmation",
        "Customer satisfaction survey",
        "Support documentation",
        "Future order discounts"
      ],
      nextSteps: "Ongoing partnership and repeat orders"
    }
  ];

  const processTimeline = [
    { phase: "Consultation", duration: "1 day", color: "bg-primary" },
    { phase: "Sourcing", duration: "2-5 days", color: "bg-accent" },
    { phase: "Quality Check", duration: "3-7 days", color: "bg-success" },
    { phase: "Documentation", duration: "2-4 days", color: "bg-warning" },
    { phase: "Shipping", duration: "14-21 days", color: "bg-secondary" },
    { phase: "Support", duration: "Ongoing", color: "bg-primary" }
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Your Export Journey, Step by Step
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our proven 6-step process ensures a smooth, transparent, and successful export experience from initial consultation to final delivery.
          </p>
        </motion.div>

        {/* Process Timeline Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-primary text-center mb-8">Process Timeline</h3>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {processTimeline.map((phase, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded-full ${phase.color}`}></div>
                <span className="text-sm font-medium text-text-primary">{phase.phase}</span>
                <span className="text-xs text-text-secondary">({phase.duration})</span>
                {index < processTimeline.length - 1 && (
                  <Icon name="ArrowRight" size={16} className="text-text-muted ml-2" />
                )}
              </div>
            ))}
          </div>
          <div className="text-center text-text-secondary">
            <strong>Total Timeline: 22-40 days</strong> from consultation to delivery
          </div>
        </motion.div>

        {/* Interactive Process Steps */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Step Navigation */}
          <div className="space-y-3 lg:space-y-4">
            {processSteps.map((step, index) => (
              <motion.button
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`w-full text-left p-4 sm:p-6 rounded-lg transition-all duration-300 ${
                  activeStep === index
                    ? 'bg-primary text-white shadow-elevation-lg'
                    : 'glass-card hover:shadow-elevation-md'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
              >
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activeStep === index ? 'bg-white/20' : 'bg-primary'
                  }`}>
                    <Icon 
                      name={step.icon} 
                      size={20} 
                      className="sm:w-6 sm:h-6"
                      color={activeStep === index ? 'white' : 'white'} 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`font-bold text-sm sm:text-base ${
                        activeStep === index ? 'text-white' : 'text-primary'
                      }`}>
                        Step {index + 1}: {step.title}
                      </h3>
                      <span className={`text-xs sm:text-sm flex-shrink-0 ml-2 ${
                        activeStep === index ? 'text-white/80' : 'text-text-secondary'
                      }`}>
                        {step.duration}
                      </span>
                    </div>
                    <p className={`text-xs sm:text-sm ${
                      activeStep === index ? 'text-white/90' : 'text-text-secondary'
                    }`}>
                      {step.subtitle}
                    </p>
                  </div>
                  <Icon 
                    name="ChevronRight" 
                    size={16} 
                    className={`flex-shrink-0 sm:w-5 sm:h-5 ${activeStep === index ? 'text-white' : 'text-text-muted'}`} 
                  />
                </div>
              </motion.button>
            ))}
          </div>

          {/* Step Details */}
          <div className="lg:sticky lg:top-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="glass-card overflow-hidden"
              >
                {/* Step Image */}
                <div className="relative h-48 sm:h-64 overflow-hidden">
                  <Image
                    src={processSteps[activeStep].image}
                    alt={processSteps[activeStep].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-4 sm:left-6 right-4 sm:right-6">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">
                      {processSteps[activeStep].title}
                    </h3>
                    <p className="text-sm sm:text-base text-white/90">{processSteps[activeStep].subtitle}</p>
                  </div>
                </div>

                {/* Step Content */}
                <div className="p-4 sm:p-6 lg:p-8">
                  <p className="text-text-secondary mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                    {processSteps[activeStep].description}
                  </p>

                  {/* Key Activities */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="font-semibold text-primary mb-2 sm:mb-3 text-sm sm:text-base">Key Activities:</h4>
                    <div className="space-y-1.5 sm:space-y-2">
                      {processSteps[activeStep].activities.map((activity, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary text-xs sm:text-sm leading-tight">{activity}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Deliverables */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="font-semibold text-primary mb-2 sm:mb-3 text-sm sm:text-base">Deliverables:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
                      {processSteps[activeStep].deliverables.map((deliverable, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Icon name="FileText" size={12} className="text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-text-secondary text-xs sm:text-sm leading-tight">{deliverable}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="p-3 sm:p-4 bg-surface-100 rounded-lg">
                    <h4 className="font-semibold text-primary mb-1 sm:mb-2 text-sm sm:text-base">What's Next:</h4>
                    <p className="text-text-secondary text-xs sm:text-sm leading-tight">{processSteps[activeStep].nextSteps}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Action Buttons */}
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => navigate('/contact')}
                className="flex-1 btn-accent py-3 font-semibold flex items-center justify-center space-x-2 text-sm sm:text-base"
              >
                <Icon name="Play" size={16} className="sm:w-5 sm:h-5" />
                <span>Start This Process</span>
              </button>
            </div>
          </div>
        </div>

        {/* Process Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="mt-20 glass-card p-8"
        >
          <h3 className="text-2xl font-bold text-primary text-center mb-8">
            Why Our Process Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Eye" size={32} color="white" />
              </div>
              <h4 className="font-semibold text-primary mb-2">Complete Transparency</h4>
              <p className="text-text-secondary text-sm">
                Real-time updates and full visibility into every step of your export journey.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Clock" size={32} color="white" />
              </div>
              <h4 className="font-semibold text-primary mb-2">Predictable Timelines</h4>
              <p className="text-text-secondary text-sm">
                Clear milestones and realistic timelines that you can count on for planning.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-success to-success-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={32} color="white" />
              </div>
              <h4 className="font-semibold text-primary mb-2">Quality Guaranteed</h4>
              <p className="text-text-secondary text-sm">
                Multiple quality checkpoints ensure your products meet exact specifications.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Process Start Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="glass-card max-w-md w-full p-8 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Rocket" size={32} color="white" />
              </div>
              <h3 className="text-2xl font-bold text-primary mb-4">Ready to Start?</h3>
              <p className="text-text-secondary mb-6">
                Let's begin your export journey with a free consultation. Our experts are ready to help you succeed.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    const element = document.getElementById('export-inquiry');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="w-full btn-accent py-3 font-semibold"
                >
                  Schedule Free Consultation
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full text-text-secondary hover:text-primary transition-colors duration-250"
                >
                  Maybe later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ExportProcess;